import React, { useState } from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';

import Button from 'CommonComponents/Button';
import SelectFromField from 'CommonComponents/SelectFromField';
import TextFormField from 'CommonComponents/TextFormField';
import Toast from 'CommonComponents/Toast';

import { useToast } from 'Hooks/useToast';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import CircleCheckIcon from 'Assets/icons/circle-check.svg';

export default function SubscriptionPlanItem(props) {
    const [edit, setEdit] = useState(false);
    const [showModules, setShowModules] = useState(false);

    const toast = useToast();

    const PlanScheme = Yup.object().shape({
        name: Yup.string().required('This value is required'),
        max_events_number: Yup.number().integer().min(1).positive().required('This value is required'),
        seats: Yup.number().integer().min(1).positive().required('This value is required'),
        trial_period_days: Yup.number().integer().min(1).positive().required('This value is required'),
        public: Yup.boolean().required('This value is required'),
        enabled: Yup.boolean().required('This value is required'),
    });

    const editPlan = (e) => {
        e.preventDefault();
        setEdit(true);
    };

    const cancelEdit = (resetForm) => {
        setEdit(false);
        setInitValues(props.plan, resetForm);
    };

    const setInitValues = (plan, resetForm) => {
        resetForm({
            values: initialValues(plan),
        });
    };

    const initialValues = (plan) => {
        return {
            name: plan.name,
            max_events_number: plan.max_events_number,
            seats: plan.seats,
            trial_period_days: plan.trial_period_days,
            public: plan.public,
            enabled: plan.enabled,
        };
    };

    const toggleModules = (e) => {
        e.preventDefault();
        setShowModules(!showModules);
    };

    return (
        <div className="tw-col-span-1">
            <div className="table__card">
                <h4 className="tw-text-center tw-mb-4">{props.plan.name}</h4>
                <Formik
                    initialValues={initialValues(props.plan)}
                    validationSchema={PlanScheme}
                    onSubmit={(values, actions) => {
                        let data = { ...values };
                        setEdit(false);

                        axios
                            .put(props.updateUrl, data)
                            .then((response) => {
                                toast.success($t('The subscription plan updated successfully'));
                                setInitValues(actions.resetForm, response.data.plan);
                                setEdit(false);
                            })
                            .catch((e) => {
                                actions.setErrors(e.response.data.errors);
                            })
                            .finally(() => {
                                actions.setSubmitting(false);
                            });
                    }}
                >
                    {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue, setFieldTouched, initialValues, resetForm }) => (
                        <>
                            <div className="tw-grid tw-grid-cols-1 tw-mb-3">
                                <TextFormField
                                    label={$t('Plan Name')}
                                    required={true}
                                    type="text"
                                    placeholder="Plan name"
                                    name="name"
                                    disabled={!edit}
                                    horizontalLayout={true}
                                ></TextFormField>
                            </div>
                            <div className="tw-grid tw-grid-cols-1 tw-mb-3">
                                <TextFormField
                                    label={$t('Trial Period Days')}
                                    required={true}
                                    type="number"
                                    placeholder={$t('Trial Period Days')}
                                    name="trial_period_days"
                                    disabled={!edit}
                                    horizontalLayout={true}
                                ></TextFormField>
                            </div>
                            <div className="tw-grid tw-grid-cols-1 tw-mb-3">
                                <TextFormField
                                    label={$t('Max Events')}
                                    required={true}
                                    type="text"
                                    placeholder={$t('Max Events')}
                                    name="max_events_number"
                                    disabled={!edit}
                                    horizontalLayout={true}
                                ></TextFormField>
                            </div>
                            <div className="tw-grid tw-grid-cols-1 tw-mb-3">
                                <TextFormField
                                    label={$t('Max Users')}
                                    required={true}
                                    type="text"
                                    placeholder={$t('Max Users')}
                                    name="seats"
                                    disabled={!edit}
                                    horizontalLayout={true}
                                ></TextFormField>
                            </div>
                            <div className="tw-grid tw-grid-cols-1 tw-mb-3">
                                <SelectFromField label={$t('Public')} as="select" name="public" disabled={!edit} horizontalLayout={true}>
                                    <option value={0}>No</option>
                                    <option value={1}>Yes</option>
                                </SelectFromField>
                            </div>
                            <div className="tw-grid tw-grid-cols-1 tw-mb-3">
                                <SelectFromField label={$t('Enabled')} as="select" name="enabled" disabled={!edit} horizontalLayout={true}>
                                    <option value={0}>No</option>
                                    <option value={1}>Yes</option>
                                </SelectFromField>
                            </div>
                            <div className="tw-flex tw-justify-start tw-mt-4 tw-cursor-pointer">
                                <a href="" onClick={toggleModules} className="tw-flex">
                                    <span className="tw-mr-2">{$t('Modules')}</span>
                                    <span className="tw-flex tw-items-center">{showModules ? <ChevronUpIcon /> : <ChevronDownIcon />}</span>
                                </a>
                            </div>

                            {showModules && (
                                <div className="tw-grid tw-grid-cols-1 tw-mb-3">
                                    {props.plan.modulesWithSubModules.map((module) => {
                                        return (
                                            <div key={module.id}>
                                                <span className="tw-flex tw-items-center tw-mr-3">
                                                    <CircleCheckIcon className="tw-w-3 tw-h-3 tw-text-green" aria-hidden="true" />
                                                    <span className="tw-ml-2">{` ${module.name}`}</span>
                                                </span>
                                                {module.sub_modules.length > 0 && (
                                                    <ul>
                                                        {module.sub_modules.map((subModule) => {
                                                            return (
                                                                <li key={subModule.id}>
                                                                    <span className="tw-flex tw-items-center tw-ml-4">
                                                                        <CircleCheckIcon className="tw-w-3 tw-h-3 tw-text-green" aria-hidden="true" />
                                                                        <span className="tw-ml-2">{` ${subModule.name}`}</span>
                                                                    </span>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {!edit && (
                                <div className="tw-flex tw-justify-end tw-mt-6">
                                    <a href="" onClick={editPlan}>
                                        {$t('Edit')}
                                    </a>
                                </div>
                            )}

                            {edit && (
                                <div className="tw-flex tw-justify-end tw-mt-6">
                                    <Button variant="primary" disabled={isSubmitting} onClick={handleSubmit} className="tw-mr-4">
                                        {$t('Update')}
                                    </Button>
                                    <Button variant="danger" onClick={() => cancelEdit(resetForm)}>
                                        {$t('Cancel')}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </Formik>
            </div>
            <Toast id="plan-update-toast" message="The subscription plan updated successfully" />
        </div>
    );
}
